import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Megaphone, Search, TrendingUp, Palette, Sparkles } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import MagneticButton from "../MagneticButton";

const EASE = [0.22, 1, 0.36, 1];

const ICON_MAP = {
  megaphone: Megaphone,
  search: Search,
  "trending-up": TrendingUp,
  palette: Palette,
};

function StaggeredHeading({ words, accentIndices = [], testId }) {
  return (
    <h1
      data-testid={testId}
      className="font-display text-display-1 font-semibold text-ink"
      style={{ fontVariationSettings: '"opsz" 144' }}
    >
      {words.map((w, i) => (
        <motion.span
          key={i}
          initial={{ opacity: 0, y: "0.4em" }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 + i * 0.07, ease: EASE }}
          className={
            "inline-block whitespace-pre " +
            (accentIndices.includes(i) ? "gold-foil-text" : "")
          }
        >
          {w}
          {i < words.length - 1 ? "\u00A0" : ""}
        </motion.span>
      ))}
    </h1>
  );
}

export default function ServiceHero({ service, content, onCtaClick }) {
  const { t, isRTL } = useLanguage();
  const Icon = ICON_MAP[service?.icon] || Sparkles;

  const pre = (content?.hero_title_pre || "").trim();
  const accent = (content?.hero_title_accent || "").trim();
  const post = (content?.hero_title_post || "").trim();
  const preWords = pre ? pre.split(/\s+/).filter(Boolean) : [];
  const accentWord = accent;
  const postWords = post ? post.split(/\s+/).filter(Boolean) : [];
  const allWords = [...preWords, accentWord, ...postWords].filter(Boolean);
  const accentIdx = preWords.length;

  return (
    <section
      data-testid="service-hero"
      className="relative overflow-hidden bg-cream pt-12 pb-20 sm:pt-20 sm:pb-section-lg"
    >
      <div className="pointer-events-none absolute -top-32 end-[-12%] h-[560px] w-[560px] rounded-full bg-gold/15 blur-3xl" />
      <div className="pointer-events-none absolute inset-0 bg-grain opacity-50" />

      <div className="container-page relative grid gap-10 lg:grid-cols-12 lg:gap-12">
        <div className="lg:col-span-7 flex flex-col justify-center">
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="eyebrow mb-6"
            data-testid="service-hero-eyebrow"
          >
            <Icon className="h-3.5 w-3.5 text-gold" />
            {content?.hero_eyebrow}
          </motion.span>

          <StaggeredHeading
            words={allWords}
            accentIndices={[accentIdx]}
            testId="service-hero-title"
          />

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6, ease: EASE }}
            className="mt-7 max-w-2xl text-body-lg text-ink/70"
            data-testid="service-hero-subtitle"
          >
            {content?.hero_subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.85 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <MagneticButton strength={12}>
              <button
                type="button"
                onClick={onCtaClick}
                className="btn-gold group"
                data-testid="service-hero-cta-primary"
              >
                <span>{t("service.cta_strategy_call")}</span>
                <ArrowRight className={`h-4 w-4 transition-transform group-hover:translate-x-1 ${isRTL ? "rotate-180" : ""}`} />
              </button>
            </MagneticButton>
            <a href="#pricing" className="btn-outline-ink" data-testid="service-hero-cta-secondary">
              {t("service.cta_view_pricing")}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1.05 }}
            className="mt-12 flex flex-wrap items-center gap-x-6 gap-y-2 text-micro text-ink/55"
          >
            {[content?.metric_1, content?.metric_2, content?.metric_3].filter(Boolean).map((m, i) => (
              <React.Fragment key={i}>
                {i > 0 ? <span className="h-1 w-1 rounded-full bg-gold" /> : null}
                <span>{m}</span>
              </React.Fragment>
            ))}
          </motion.div>
        </div>

        {/* Visual */}
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.45, ease: EASE }}
          className="lg:col-span-5 flex items-center justify-center lg:justify-end"
          aria-hidden="true"
        >
          <div className="relative aspect-square w-full max-w-[440px]">
            <div className="absolute inset-0 rounded-full bg-gold-radial opacity-90" />
            <motion.svg viewBox="0 0 400 400" className="absolute inset-0"
              animate={{ rotate: 360 }} transition={{ duration: 50, ease: "linear", repeat: Infinity }}>
              <circle cx="200" cy="200" r="180" fill="none" stroke="#D4AF37" strokeWidth="0.75"
                strokeDasharray="2 8" opacity="0.55" />
            </motion.svg>
            <motion.svg viewBox="0 0 400 400" className="absolute inset-0"
              animate={{ rotate: -360 }} transition={{ duration: 80, ease: "linear", repeat: Infinity }}>
              <circle cx="200" cy="200" r="120" fill="none" stroke="#D4AF37" strokeWidth="1"
                strokeDasharray="50 250" opacity="0.85" />
            </motion.svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <Icon className="h-20 w-20 text-gold" strokeWidth={1.25} />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
